import {
  inferFailedToolCallLabel,
  resolveToolCallLabels,
} from '@terra/shared-dto-chat-types';

export type ChatHeadlineKind =
  | 'use-skill'
  | 'analyze-source'
  | 'analyze-data'
  | 'explore'
  | 'search-web'
  | 'recall'
  | 'recon'
  | 'operate'
  | 'research'
  | 'record'
  | 'validate'
  | 'build-skill'
  | 'background-attack'
  | 'investigate-execution'
  | 'passthrough';

export type ChatHeadlineSurface =
  | 'explored'
  | 'used-skill'
  | 'analyze-source'
  | 'search-web'
  | 'recall';

export type ChatHeadlineCopy = {
  running: string;
  done: string;
  failed: string;
};

const PARENT_HEADLINE_KIND: Record<string, ChatHeadlineKind> = {
  'code-analyst': 'analyze-source',
  'application-data-analyst': 'analyze-data',
  recon: 'recon',
  operator: 'operate',
  intelligence: 'research',
  'object-creator': 'record',
  validator: 'validate',
  'skill-builder': 'build-skill',
  'background-attack': 'background-attack',
  neo: 'background-attack',
  'attacker-tool': 'background-attack',
  'trace-investigation-tool': 'investigate-execution',
  'trace-investigation': 'investigate-execution',
};

const HEADLINE_TOOL_NAME: Partial<Record<ChatHeadlineKind, string>> = {
  ...Object.fromEntries(
    [...Object.entries(PARENT_HEADLINE_KIND)]
      .reverse()
      .map(([toolName, kind]) => [kind, toolName]),
  ),
  'search-web': 'websearch-tool',
  recall: 'get-memory-tool',
};

const CHILD_HEADLINE_COPY: Record<
  'use-skill' | 'explore',
  Pick<ChatHeadlineCopy, 'running' | 'done'>
> = {
  'use-skill': {
    running: 'Using skill...',
    done: 'Used skill.',
  },
  explore: {
    running: 'Exploring...',
    done: 'Explored',
  },
};

export function resolveWorkHeadlineKind({
  unitKind,
  parentKind,
}: {
  unitKind: ChatHeadlineKind;
  parentKind: ChatHeadlineKind;
}): ChatHeadlineKind {
  if (parentKind === 'passthrough') {
    return unitKind;
  }

  return parentKind;
}

function normalizeToolName(toolName: string): string {
  return toolName.replace(/_/g, '-').toLowerCase();
}

export function isPersistentSubAgentTool(
  toolName: string | undefined,
): boolean {
  if (!toolName) {
    return false;
  }

  const kind = PARENT_HEADLINE_KIND[normalizeToolName(toolName)];

  return kind !== undefined && kind !== 'background-attack';
}

export function headlineKindForParentTool(
  toolName: string | undefined,
): ChatHeadlineKind {
  if (!toolName) {
    return 'passthrough';
  }

  return PARENT_HEADLINE_KIND[normalizeToolName(toolName)] ?? 'passthrough';
}

export function headlineKindForSurface(
  surface: ChatHeadlineSurface,
): ChatHeadlineKind {
  switch (surface) {
    case 'used-skill':
      return 'use-skill';
    case 'analyze-source':
      return 'analyze-source';
    case 'search-web':
      return 'search-web';
    case 'recall':
      return 'recall';
    case 'explored':
      return 'explore';
  }
}

export function surfaceForHeadlineKind(
  kind: ChatHeadlineKind,
): ChatHeadlineSurface | undefined {
  switch (kind) {
    case 'use-skill':
      return 'used-skill';
    case 'analyze-source':
      return 'analyze-source';
    case 'search-web':
      return 'search-web';
    case 'recall':
      return 'recall';
    case 'explore':
      return 'explored';
    default:
      return undefined;
  }
}

export function activityUsesOwnChrome(kind: ChatHeadlineKind): boolean {
  return kind === 'use-skill';
}

export function stoppedHeadlineLabel(running: string): string {
  const base = running.trim().replace(/\.\.\.$/, '');

  if (!base) {
    return 'Stopped';
  }

  return `Stopped ${base.charAt(0).toLowerCase()}${base.slice(1)}`;
}

export function getHeadlineCopy(kind: ChatHeadlineKind): ChatHeadlineCopy {
  if (kind === 'use-skill' || kind === 'explore') {
    const copy = CHILD_HEADLINE_COPY[kind];

    return {
      ...copy,
      failed: inferFailedToolCallLabel(copy.running),
    };
  }

  const toolName = HEADLINE_TOOL_NAME[kind];

  if (!toolName) {
    return {
      running: 'Working...',
      done: 'Done',
      failed: inferFailedToolCallLabel('Working...'),
    };
  }

  const labels = resolveToolCallLabels({ toolName });

  return {
    running: labels.labelPresent,
    done: labels.labelPast,
    failed: labels.labelFailed,
  };
}

export function headlineToolName(kind: ChatHeadlineKind): string {
  return HEADLINE_TOOL_NAME[kind] ?? '__headline__';
}
